// Shared TanStack Query client config for the Civala SPAs.
//
// We unify the *client config* (not the hooks) on purpose: the retry / staleTime
// behavior is the part that drifts between apps and the part that matters for
// resilience against the Cloudflare Pages serving spikes + dropped requests on the
// gated hosts (civala-ai/civala.ai#168). Each app keeps its own query hooks /
// query-option factories (they're endpoint-specific) but builds its QueryClient
// from this factory so the resilience defaults stay identical.

import { createLogger } from "@civala/shared/logging"
import { QueryCache, QueryClient } from "@tanstack/react-query"
import type { Either, HttpError } from "functype"

const isUnauthorized = (error: unknown): boolean =>
  typeof error === "object" && error !== null && "status" in error && (error as { status: unknown }).status === 401

/**
 * Unwrap a functype `Either<HttpError, T>` for a TanStack Query `queryFn` — return the
 * value, or throw a **status-bearing** Error on the Left so the shared 401-bounce (and
 * any other status-aware handling) fires uniformly. Use as `.then(unwrapOrThrow)`.
 *
 * (A plain `throw new Error(message)` loses the HTTP status, which is why the 401-bounce
 * silently no-ops; this preserves `error.status`.)
 *
 * @interop TanStack Query marks a query failed only when its queryFn throws or rejects.
 */
export const unwrapOrThrow = <T>(either: Either<HttpError, T>): T =>
  either.fold(
    (err) => {
      const message =
        err._tag === "HttpStatusError"
          ? `HTTP ${err.status} ${err.statusText}${err.body ? ` — ${err.body.slice(0, 200)}` : ""}`
          : err._tag === "NetworkError"
            ? "could not reach the API"
            : "unexpected response from the API"
      const error: Error & { status?: number } = new Error(message)
      if (err._tag === "HttpStatusError") error.status = err.status
      throw error
    },
    (value) => value,
  )

export type CreateQueryClientOptions = {
  /** App name for the structured logger (e.g. "nexus") — the createLogger app union. */
  readonly app: Parameters<typeof createLogger>[0]["app"]
  /**
   * Path to bounce to on a 401 so the Cloudflare Access cookie middleware can
   * re-challenge an expired session. Set to null to disable. Default "/".
   */
  readonly unauthorizedRedirect?: string | null
}

/**
 * Build a QueryClient with Civala's shared defaults:
 *  - staleTime 30s → revisits serve cached data instantly (the slow path is skipped
 *    while still fresh), and stale-while-revalidate refetches in the background so a
 *    slow request never blocks the UI.
 *  - retry transient failures (network drops / 5xx) with exponential backoff, so a
 *    one-off Cloudflare spike or dropped request becomes a retried success — but
 *    NEVER retry a 401 (it won't recover; bounce to re-auth instead).
 *  - on any query error: log it, and on 401 bounce to `unauthorizedRedirect`.
 */
export const createQueryClient = ({ app, unauthorizedRedirect = "/" }: CreateQueryClientOptions): QueryClient => {
  const log = createLogger({ app })
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        log.withMetadata({ error: error instanceof Error ? error.message : error }).error("query failed")
        if (unauthorizedRedirect !== null && isUnauthorized(error) && typeof window !== "undefined") {
          window.location.assign(unauthorizedRedirect)
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => (isUnauthorized(error) ? false : failureCount < 2),
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
    },
  })
}
